/**
 * Affidavit / return templates (AFF-001, AFF-006). Templates are agency data;
 * the defaults below are placeholders for the pilot and must be reviewed by
 * counsel before production use (OPEN-007).
 */

export type AffidavitOutcome = 'served' | 'non_served';

export interface AffidavitTemplate {
  id: string;
  name: string;
  match: { paperTypes?: string[]; courtIds?: string[]; outcome: AffidavitOutcome | 'any' };
  title: string;
  letterhead: string[];
  body: string[];
  requiredFields: (keyof AffidavitFields)[];
  signatureBlock: string[];
}

/** Affidavit-only fields. Corrections here never alter the service record (AFF-002). */
export interface AffidavitFields {
  agencyName: string;
  county: string;
  paperNumber: string;
  caseNumber: string;
  court: string;
  plaintiff: string;
  defendant: string;
  paperType: string;
  address: string;
  outcome: string;
  serviceDate: string;
  serviceTime: string;
  methodDescription: string;
  recipientName: string;
  recipientRelationship: string;
  recipientAge: string;
  attemptsSummary: string;
  nonServiceReason: string;
  propertyDescription: string;
  supervisorAction: string;
  serverName: string;
  serverTitle: string;
}

export const AFFIDAVIT_FIELD_LABELS: Record<keyof AffidavitFields, string> = {
  agencyName: 'Agency',
  county: 'County',
  paperNumber: 'Paper number',
  caseNumber: 'Case number',
  court: 'Court',
  plaintiff: 'Plaintiff / petitioner',
  defendant: 'Defendant / respondent',
  paperType: 'Paper type',
  address: 'Service address',
  outcome: 'Outcome',
  serviceDate: 'Service date',
  serviceTime: 'Service time',
  methodDescription: 'Manner of service',
  recipientName: 'Recipient name',
  recipientRelationship: 'Recipient relationship',
  recipientAge: 'Recipient age',
  attemptsSummary: 'Attempts',
  nonServiceReason: 'Reason not served',
  propertyDescription: 'Property found',
  supervisorAction: 'Action taken',
  serverName: 'Server name',
  serverTitle: 'Server title',
};

export const DEFAULT_AFFIDAVIT_TEMPLATES: AffidavitTemplate[] = [
  {
    id: 'default-served',
    name: 'Return of Service — Served (default)',
    match: { outcome: 'served' },
    title: 'AFFIDAVIT / RETURN OF SERVICE',
    letterhead: ['{{agencyName}}', 'Civil Process Division — {{county}}'],
    body: [
      'Court: {{court}}    Case No.: {{caseNumber}}',
      '{{plaintiff}}, Plaintiff/Petitioner, v. {{defendant}}, Defendant/Respondent.',
      'Document(s) served: {{paperType}} (Paper No. {{paperNumber}}).',
      'I certify that on {{serviceDate}} at {{serviceTime}}, at {{address}}, I served the above-named defendant by {{methodDescription}}.',
      '{{attemptsSummary}}',
    ],
    requiredFields: ['caseNumber', 'defendant', 'address', 'serviceDate', 'serviceTime', 'methodDescription', 'serverName'],
    signatureBlock: ['{{serverName}}', '{{serverTitle}}, {{agencyName}}'],
  },
  {
    id: 'default-non-served',
    name: 'Return of Non-Service (default)',
    match: { outcome: 'non_served' },
    title: 'AFFIDAVIT / RETURN OF NON-SERVICE',
    letterhead: ['{{agencyName}}', 'Civil Process Division — {{county}}'],
    body: [
      'Court: {{court}}    Case No.: {{caseNumber}}',
      '{{plaintiff}}, Plaintiff/Petitioner, v. {{defendant}}, Defendant/Respondent.',
      'Document(s): {{paperType}} (Paper No. {{paperNumber}}).',
      'I certify that after diligent effort I was unable to serve the above-named defendant at {{address}}. Reason: {{nonServiceReason}}.',
      'Attempts made:\n{{attemptsSummary}}',
    ],
    requiredFields: ['caseNumber', 'defendant', 'address', 'nonServiceReason', 'attemptsSummary', 'serverName'],
    signatureBlock: ['{{serverName}}', '{{serverTitle}}, {{agencyName}}'],
  },
];

export function selectTemplate(
  templates: AffidavitTemplate[],
  ctx: { paperTypeCode: string; courtId: string | null; outcome: AffidavitOutcome },
): AffidavitTemplate {
  let best: AffidavitTemplate | null = null;
  let bestScore = -1;
  for (const t of templates) {
    if (t.match.outcome !== 'any' && t.match.outcome !== ctx.outcome) continue;
    if (t.match.paperTypes?.length && !t.match.paperTypes.includes(ctx.paperTypeCode)) continue;
    if (t.match.courtIds?.length && (!ctx.courtId || !t.match.courtIds.includes(ctx.courtId))) continue;
    const score = (t.match.courtIds?.length ? 4 : 0) + (t.match.paperTypes?.length ? 2 : 0) + (t.match.outcome !== 'any' ? 1 : 0);
    if (score > bestScore) {
      best = t;
      bestScore = score;
    }
  }
  if (!best) throw new Error(`No affidavit template for ${ctx.paperTypeCode}/${ctx.outcome}`);
  return best;
}

export interface RenderedAffidavit {
  templateId: string;
  title: string;
  letterhead: string[];
  paragraphs: string[];
  signatureBlock: string[];
  missingRequired: (keyof AffidavitFields)[];
}

function fill(s: string, f: Partial<AffidavitFields>): string {
  return s.replace(/\{\{(\w+)\}\}/g, (_m, k: string) => (f as Record<string, string | undefined>)[k] ?? '');
}

export function renderAffidavit(t: AffidavitTemplate, fields: Partial<AffidavitFields>): RenderedAffidavit {
  return {
    templateId: t.id,
    title: fill(t.title, fields),
    letterhead: t.letterhead.map((l) => fill(l, fields)),
    paragraphs: t.body.map((l) => fill(l, fields)).filter((l) => l.trim().length > 0),
    signatureBlock: t.signatureBlock.map((l) => fill(l, fields)),
    missingRequired: t.requiredFields.filter((k) => !fields[k]?.toString().trim()),
  };
}

export function describeMethod(
  method: 'personal' | 'substitute' | 'posting' | null | undefined,
  recipient?: { name?: string; relationship?: string; age?: number | null; estimatedAge?: number | null } | null,
): string {
  switch (method) {
    case 'personal':
      return 'delivering a copy to the defendant personally';
    case 'substitute': {
      const age = recipient?.age ?? recipient?.estimatedAge;
      return `leaving a copy at the defendant's dwelling with ${recipient?.name ?? '[recipient]'} (${recipient?.relationship ?? 'relationship'}${
        age != null ? `, age ${recipient?.age != null ? '' : 'approx. '}${age}` : ''
      }), a person of suitable age and discretion residing there who is not a party to the action`;
    }
    case 'posting':
      return 'posting a copy conspicuously on the premises';
    default:
      return '';
  }
}
